import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { enqueueSnackbar } from 'notistack';
import {
  ExportHubAPI,
  ExportProduct,
  ExportLicense,
  LicenseDrawdown,
} from 'api/ExportHubAPI';
import { formatCurrency } from './licensePresets';
import RecordDrawdownModal from './RecordDrawdownModal';

const STATUS_VARIANT: Record<string, string> = {
  Posted: 'success',
  Draft: 'warning',
  Void: 'muted',
};

interface Props {
  product: ExportProduct;
  groupId: string | undefined;
  licenses: ExportLicense[];
  onChanged: () => void;
}

/**
 * Every transaction recorded for one product, across all of its licenses.
 *
 * The license-side ledger cannot answer this — it is scoped to one license, and
 * a product is typically on several. The lifetime average unit value in the
 * footer is the closest thing to "this product's value" that is honestly
 * derivable: it is what past shipments actually agreed, not a catalog price.
 */
const ProductTransactions: React.FC<Props> = ({
  product,
  groupId,
  licenses,
  onChanged,
}) => {
  const [rows, setRows] = useState<LicenseDrawdown[]>([]);
  const [loading, setLoading] = useState(true);
  const [recordOpen, setRecordOpen] = useState(false);

  const load = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    try {
      setRows(await ExportHubAPI.getProductDrawdowns(product.id, groupId));
    } catch (err) {
      console.error('Failed to load product transactions:', err);
      enqueueSnackbar('Failed to load transactions.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [product.id, groupId]);

  useEffect(() => {
    load();
  }, [load]);

  const totals = useMemo(() => {
    const posted = rows.filter((r) => r.status === 'Posted');
    const value = posted.reduce((s, r) => s + r.value, 0);
    const qty = posted.reduce((s, r) => s + (r.quantity ?? 0), 0);
    // Average over quantity, not over rows: a 20-unit shipment should weigh
    // twenty times a 1-unit one.
    const priced = posted.filter(
      (r) => r.unit_value !== null && r.quantity !== null && r.quantity !== 0,
    );
    const pricedQty = priced.reduce((s, r) => s + Math.abs(r.quantity ?? 0), 0);
    const pricedValue = priced.reduce((s, r) => s + Math.abs(r.value), 0);
    return {
      count: posted.length,
      value: value,
      quantity: qty,
      avgUnit: pricedQty > 0 ? pricedValue / pricedQty : null,
    };
  }, [rows]);

  const licenseLabel = (d: LicenseDrawdown): string =>
    d.license_number || d.license_type || '—';

  return (
    <>
      <div className='eh-panel'>
        <div className='eh-panel-title eh-panel-title-flex'>
          <span>
            Transactions{' '}
            <span className='eh-count-soft'>
              (all licenses · {rows.length})
            </span>
          </span>
          <div className='eh-panel-head-actions'>
            <button
              className='eh-mini-btn'
              type='button'
              onClick={() => setRecordOpen(true)}
              disabled={licenses.length === 0}
              title={
                licenses.length === 0
                  ? 'Link a license before recording a transaction'
                  : undefined
              }
            >
              <AddIcon style={{ fontSize: 15 }} /> Record transaction
            </button>
          </div>
        </div>

        {loading ? (
          <div className='eh-table-skeleton'>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className='eh-skel eh-skel-row' />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className='eh-detail-note'>
            {licenses.length === 0
              ? 'No transactions yet. Link a license to this product, then record shipments against it.'
              : 'No transactions yet. Each shipment draws down a license at that shipment’s own price.'}
          </div>
        ) : (
          <div className='eh-dd-table-scroll'>
            <table className='eh-records-table eh-dd-table'>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>License</th>
                  <th>Reference</th>
                  <th className='eh-dd-num'>Qty</th>
                  <th className='eh-dd-num'>Unit value</th>
                  <th className='eh-dd-num'>Value</th>
                  <th>Destination</th>
                  <th>Consignee</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => (
                  <tr
                    key={d.id}
                    className={d.status === 'Void' ? 'eh-dd-void' : undefined}
                  >
                    <td>{moment(d.transaction_date).format('MM/DD/YYYY')}</td>
                    <td className='eh-mono'>{licenseLabel(d)}</td>
                    <td className='eh-mono'>{d.reference || '—'}</td>
                    <td className='eh-dd-num'>
                      {d.quantity === null ? '—' : d.quantity}
                      {d.quantity !== null && d.unit_of_measure ? (
                        <span className='eh-dd-uom-tag'>
                          {' '}
                          {d.unit_of_measure}
                        </span>
                      ) : null}
                    </td>
                    <td className='eh-dd-num'>
                      {d.unit_value === null
                        ? '—'
                        : formatCurrency(d.unit_value)}
                      {d.value_overridden && (
                        <span
                          className='eh-dd-flag'
                          title='Total was typed, not calculated from quantity × unit value'
                        >
                          <EditOutlinedIcon style={{ fontSize: 12 }} />
                        </span>
                      )}
                    </td>
                    <td className='eh-dd-num eh-dd-value'>
                      {formatCurrency(d.value)}
                    </td>
                    <td>{d.destination_country || '—'}</td>
                    <td>{d.consignee || '—'}</td>
                    <td>
                      <span
                        className={`eh-pill eh-pill-${
                          STATUS_VARIANT[d.status] || 'muted'
                        }`}
                      >
                        {d.drawdown_type === 'Return' && d.status === 'Posted'
                          ? 'Return'
                          : d.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3}>Lifetime · {totals.count} posted</td>
                  <td className='eh-dd-num'>{totals.quantity}</td>
                  <td className='eh-dd-num eh-dd-foot-note'>
                    {totals.avgUnit === null
                      ? '—'
                      : `avg ${formatCurrency(totals.avgUnit)}`}
                  </td>
                  <td className='eh-dd-num eh-dd-value'>
                    {formatCurrency(totals.value)}
                  </td>
                  <td colSpan={3} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      <RecordDrawdownModal
        open={recordOpen}
        groupId={groupId}
        licenses={licenses}
        lockedProductId={product.id}
        onClose={() => setRecordOpen(false)}
        onSaved={() => {
          setRecordOpen(false);
          load();
          onChanged();
        }}
      />
    </>
  );
};

export default ProductTransactions;
